// @weconsultrx/site-chrome
//
// The ONE definition of weconsultrx.com's header nav, footer links, logo
// assets and brand colours. Two renderers read it:
//   - apps/landing/src/layout/{Header,Footer}.tsx (React, the marketing site)
//   - packages/blog-engine/src/render.ts (server-rendered HTML for /blog,
//     served by apps/api behind the edge Worker)
// They cannot share markup (React vs. string HTML in a different app), so they
// share the data instead: a nav item, label, order, link target or brand colour
// changed here changes on both surfaces. Pure data, no imports, so it is safe
// in the landing bundle and in Node.

export interface NavChild {
  label: string;
  to: string;
  desc?: string;
}

export interface NavItem {
  label: string;
  /** Section overview page, if the section has one. */
  to?: string;
  children?: NavChild[];
}

/** Primary header nav, in display order. Paths are relative to the marketing site. */
export const PRIMARY_NAV: NavItem[] = [
  {
    label: "Platform",
    to: "/platform",
    children: [
      { label: "Platform overview", to: "/platform", desc: "End-to-end workflow + architecture" },
      { label: "Features", to: "/features", desc: "Clinical engine · synthesis · bundles · invoicing" },
    ],
  },
  {
    label: "Solutions",
    children: [
      { label: "Long-Term Care (SNF & ALF)", to: "/solutions/snf", desc: "ASCP · F605 / F757 / F881 · same pack for assisted living" },
      { label: "Ambulatory Surgical (ASC)", to: "/solutions/asc", desc: "AAAHC pharma standards · DEA · vaccine temp" },
      { label: "Dialysis", to: "/solutions/dialysis", desc: "CMS CfC §494 · AAMI water" },
    ],
  },
  { label: "Pricing", to: "/pricing" },
  {
    label: "Resources",
    children: [
      { label: "Resource hub", to: "/resources" },
      { label: "Blog", to: "/blog" },
      { label: "Docs", to: "/docs" },
      { label: "Trust center", to: "/trust" },
    ],
  },
  { label: "About", to: "/about" },
];

export const HEADER_CTA = { label: "Request a demo", to: "/demo" } as const;
export const SIGN_IN_LABEL = "Sign in";

export interface FooterLink {
  label: string;
  to?: string;
  href?: string;
}

export const FOOTER_COLUMNS: Array<{ title: string; links: FooterLink[] }> = [
  {
    title: "Platform",
    links: [
      { label: "Overview", to: "/platform" },
      { label: "Features", to: "/features" },
      { label: "Pricing", to: "/pricing" },
      { label: "Customers", to: "/customers" },
      { label: "Request demo", to: "/demo" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Long-term care (SNF)", to: "/solutions/snf" },
      { label: "Ambulatory surgical (ASC)", to: "/solutions/asc" },
      { label: "Dialysis", to: "/solutions/dialysis" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Careers", to: "/careers" },
      { label: "Press kit", to: "/press" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Resource hub", to: "/resources" },
      { label: "Blog", to: "/blog" },
      { label: "Docs", to: "/docs" },
      { label: "Compare alternatives", to: "/compare" },
      { label: "Sitemap", to: "/sitemap" },
    ],
  },
  {
    title: "Trust",
    links: [
      { label: "Trust center", to: "/trust" },
      { label: "Security", to: "/security" },
      { label: "HIPAA", to: "/hipaa" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
      { label: "DPA", to: "/dpa" },
      { label: "BAA", to: "/baa" },
      { label: "SLA", to: "/sla" },
      { label: "Subprocessors", to: "/subprocessors" },
      { label: "Status", to: "/status" },
    ],
  },
];

export const FOOTER_TAGLINE =
  "Modern consultant pharmacy operations — clinical engine, compliance automation, and AI-augmented workflow for SNF, ASC, and dialysis.";

export const FOOTER_LEGAL_LINKS: FooterLink[] = [
  { label: "Accessibility", to: "/accessibility" },
  { label: "Cookies", to: "/cookies" },
  { label: "GDPR", to: "/gdpr" },
  { label: "CCPA", to: "/ccpa" },
  { label: "Disclosure", to: "/responsible-disclosure" },
  { label: "SMS Terms", to: "/sms-terms" },
];

export const CONTACT_EMAIL = "hello@weconsultrx.com";
export const COPYRIGHT_HOLDER = "WeConsult Rx, a DRX Consulting Group company. All rights reserved.";

/** Logo files in apps/landing/public, served at the marketing site root. */
export const LOGO = {
  header: { src: "/logo-horizontal-no-tagline.svg", width: 220, height: 48 },
  footer: { src: "/logo-stacked-on-dark.svg", width: 220, height: 154 },
  alt: "WeConsult Rx",
} as const;

/** Brand colours, mirrored by apps/landing/tailwind.config.ts (a test asserts they agree). */
export const BRAND = {
  forest50: "#f0fdf4",
  forest400: "#4ade80",
  forest700: "#15803d", // primary
  forest900: "#14532d", // primary hover / deep
  ink900: "#181815",
  ink800: "#262622",
  ink700: "#40403B",
  ink600: "#55554F",
  ink500: "#666660",
  ink400: "#8E8E88",
  ink300: "#CFCFCB",
  ink200: "#E4E4E1",
  canvas: "#F4F4F1",
  surface: "#FFFFFF",
  border: "#E3E3DF",
} as const;

/** The landing site's UI font stack (tailwind fontFamily.sans). */
export const SANS_STACK =
  'ui-sans-serif, system-ui, "Inter", "Segoe UI", Roboto, "Helvetica Neue", sans-serif';

/** The landing body's paper grain (index.css --grain), multiplied over the canvas. */
export const GRAIN_DATA_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='.4'/%3E%3C/svg%3E\")";
